import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Plus, 
  Search, 
  Filter, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Target,
  Calendar,
  MoreHorizontal,
  Eye,
  Edit,
  Upload,
  Trash2,
  CheckCircle,
  Share2,
  Download,
  Loader2
} from 'lucide-react';
import { useBrandDeals, BrandDeal } from '@/hooks/useBrandDeals';

import { BrandDealEditModal } from './BrandDealEditModal';
import { LoadingFallback } from '@/components/LoadingFallback';
import { PDFDealUploadModal } from '@/components/deals/PDFDealUploadModal';
import { DealReportGenerator } from './DealReportGenerator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';

const statusColors = {
  active: 'bg-success/10 text-success border border-success/30',
  pending: 'bg-primary/10 text-primary border border-primary/30',
  completed: 'bg-muted text-muted-foreground border border-border',
  cancelled: 'bg-destructive/10 text-destructive border border-destructive/30',
};

const priorityColors = {
  high: 'bg-destructive/10 text-destructive border border-destructive/30',
  medium: 'bg-primary/10 text-primary border border-primary/30',
  low: 'bg-muted text-muted-foreground border border-border',
};

const dealTypeColors = {
  sponsorship: 'bg-primary/10 text-primary border border-primary/30',
  collaboration: 'bg-accent text-accent-foreground border border-primary/30',
  ambassador: 'bg-success/10 text-success border border-success/30',
  event: 'bg-primary/10 text-primary border border-primary/30',
};

export const BrandDealsOverview = () => {
  const navigate = useNavigate();
  const { deals, campaigns, loading, error, metrics, fetchBrandDeals, createDeal, deleteDeal } = useBrandDeals();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<BrandDeal | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportGenerator, setShowReportGenerator] = useState(false);
  const [dealToDelete, setDealToDelete] = useState<BrandDeal | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check for pending deal creation from AI chat on mount
  useEffect(() => {
    const pendingDeal = sessionStorage.getItem('pendingDealCreation');
    if (pendingDeal) {
      // Navigate to the create-deal page which will pick up the sessionStorage
      navigate('/create-deal');
    }
  }, [navigate]);

  // De-duplicate deals on the client as a safeguard - MUST be before any early returns
  const uniqueDeals = React.useMemo(() => {
    const map = new Map<string, any>();
    for (const d of deals) {
      const key = `${(d.title || '').trim().toLowerCase()}::${d.brand_profile_id || ''}::${d.deal_value || 0}::${(d.description || '').trim().toLowerCase()}`;
      if (!map.has(key)) map.set(key, d);
    }
    return Array.from(map.values());
  }, [deals]);

  // Keep selectedDeal in sync with updated deals data
  useEffect(() => {
    if (selectedDeal && uniqueDeals.length > 0) {
      const updatedDeal = uniqueDeals.find(deal => deal.id === selectedDeal.id);
      if (updatedDeal) {
        setSelectedDeal(updatedDeal);
      }
    }
  }, [uniqueDeals, selectedDeal?.id]);

  const filteredDeals = React.useMemo(() => {
    return uniqueDeals.filter(deal => {
      const matchesSearch = (deal.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (deal.talent?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (deal.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || deal.status === statusFilter;
      const matchesType = typeFilter === 'all' || deal.deal_type === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [uniqueDeals, searchTerm, statusFilter, typeFilter]);

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-muted-foreground">Loading deals and campaigns...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-destructive mb-2">Error Loading Data</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <div className="space-x-2">
              <Button 
                onClick={() => fetchBrandDeals()} 
                variant="outline"
              >
                Try Again
              </Button>
              <Button 
                onClick={() => window.location.reload()} 
                variant="secondary"
              >
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleViewDeal = (deal: BrandDeal) => {
    navigate(`/deals/${deal.id}`);
  };

  const handleEditDeal = (deal: BrandDeal) => {
    setSelectedDeal(deal);
    setShowEditModal(true);
  };

  const handleCompleteDeal = async (deal: BrandDeal) => {
    try {
      // TODO: Implement actual deal completion logic
      toast({
        title: "Deal Completed",
        description: `${deal.title} has been marked as completed.`,
      });
      fetchBrandDeals();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to complete deal. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteDeal = async () => {
    if (!dealToDelete) return;

    setIsDeleting(true);
    try {
      await deleteDeal(dealToDelete.id);
      
      toast({
        title: "Deal Deleted",
        description: `${dealToDelete.title} has been deleted.`,
      });
      
      setDealToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete deal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleShareDeal = (deal: BrandDeal) => {
    const dealUrl = `${window.location.origin}/deals/${deal.id}`;
    navigator.clipboard.writeText(dealUrl).then(() => {
      toast({
        title: "Link Copied",
        description: "Deal link has been copied to clipboard.",
      });
    }).catch(() => {
      toast({
        title: "Error",
        description: "Failed to copy link to clipboard.",
        variant: "destructive",
      });
    });
  };

  const DealCard = ({ deal }: { deal: BrandDeal }) => (
    <Card className="hover:shadow-md transition-shadow max-w-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg truncate">{deal.title || 'Untitled Deal'}</CardTitle>
            <CardDescription className="mt-1 truncate">
              {deal.talent?.name ? 
                `${deal.talent.name} • ${deal.talent.sport || 'Sport not specified'}` : 
                'Available in Marketplace'
              }
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge className={statusColors[deal.status as keyof typeof statusColors]}>
              {deal.status}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleViewDeal(deal)}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleEditDeal(deal)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Deal
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleShareDeal(deal)}>
                  <Share2 className="h-4 w-4 mr-2" />
                  Share Deal
                </DropdownMenuItem>
                {deal.status !== 'completed' && (
                  <DropdownMenuItem onClick={() => handleCompleteDeal(deal)}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark as Completed
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => setDealToDelete(deal)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Deal
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Deal Value</span>
            <span className="font-semibold">{formatCurrency(deal.deal_value || 0)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Type</span>
            <Badge variant="outline" className={dealTypeColors[deal.deal_type as keyof typeof dealTypeColors]}>
              {deal.deal_type}
            </Badge>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Priority</span>
            <Badge variant="outline" className={priorityColors[deal.priority as keyof typeof priorityColors]}>
              {deal.priority}
            </Badge>
          </div>
          {deal.estimated_completion_date && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Due Date</span>
              <span>{formatDate(deal.estimated_completion_date)}</span>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => handleViewDeal(deal)}
            >
              <Eye className="h-4 w-4 mr-1" />
              View
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => handleEditDeal(deal)}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Deals</CardTitle>
            <div className="p-2 rounded-lg bg-primary">
              <Users className="h-4 w-4 text-primary-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalDeals}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.activeDeals} active, {metrics.completedDeals} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Investment</CardTitle>
            <div className="p-2 rounded-lg bg-primary">
              <DollarSign className="h-4 w-4 text-primary-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(metrics.totalInvestment)}</div>
            <p className="text-xs text-muted-foreground">
              Avg: {formatCurrency(metrics.avgDealValue)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Campaigns</CardTitle>
            <div className="p-2 rounded-lg bg-primary">
              <Target className="h-4 w-4 text-primary-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.activeCampaigns}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.totalCampaigns} total campaigns
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Performance</CardTitle>
            <div className="p-2 rounded-lg bg-primary">
              <TrendingUp className="h-4 w-4 text-primary-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">+12%</div>
            <p className="text-xs text-muted-foreground">vs last month</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-full">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search deals or talents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="sponsorship">Sponsorship</SelectItem>
            <SelectItem value="collaboration">Collaboration</SelectItem>
            <SelectItem value="ambassador">Ambassador</SelectItem>
            <SelectItem value="event">Event</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Deals Grid */}
      <Tabs defaultValue="active" className="space-y-4 w-full max-w-full">
        <TabsList className="w-full max-w-full overflow-x-auto">
          <TabsTrigger value="active" className="relative">
            Active
            <Badge variant="secondary" className="ml-2 h-5 min-w-5 rounded-full p-0 text-xs flex items-center justify-center">
              {filteredDeals.filter(deal => deal.status === 'active').length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="posted" className="relative">
            Posted
            <Badge variant="secondary" className="ml-2 h-5 min-w-5 rounded-full p-0 text-xs flex items-center justify-center">
              {filteredDeals.filter(deal => deal.status === 'available' || deal.status === 'pending').length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {(() => {
            const activeDeals = filteredDeals.filter(deal => deal.status === 'active');
            return activeDeals.length === 0 ? (
              <Card className="p-8 text-center">
                <div className="text-muted-foreground">
                  No active deals at the moment.
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeDeals.map((deal) => (
                  <DealCard key={deal.id} deal={deal} />
                ))}
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="posted" className="space-y-4">
          {(() => {
            const postedDeals = filteredDeals.filter(deal => deal.status === 'available' || deal.status === 'pending');
            return postedDeals.length === 0 ? (
              <Card className="p-8 text-center">
                <div className="text-muted-foreground">
                  No posted deals. Create a deal to get started.
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {postedDeals.map((deal) => (
                  <DealCard key={deal.id} deal={deal} />
                ))}
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>



        
        <BrandDealEditModal
          deal={selectedDeal}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedDeal(null);
            // Refresh data after edit
            fetchBrandDeals();
          }}
        />
        
        <PDFDealUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onDealCreated={() => {
            setShowUploadModal(false);
            fetchBrandDeals();
          }}
          createDeal={createDeal}
        />

        <DealReportGenerator
          open={showReportGenerator}
          onOpenChange={setShowReportGenerator}
          deals={uniqueDeals}
          campaigns={campaigns}
          metrics={metrics}
        />

        <AlertDialog open={!!dealToDelete} onOpenChange={(open) => !open && setDealToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Deal?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{dealToDelete?.title}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteDeal}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </div>
  );
};